import { CompanyUser } from '@/api';
import { useMemo } from 'react';
import { useCompaniesTranslation } from '../hooks';

type UseEditProfileActionProps = {
    user: CompanyUser;
};

type UseEditProfileActionData = {
    /** Whether this viewer can edit this member's details. */
    isAllowed: boolean;
    /** Why not. Empty while allowed. */
    reason: string;
};

/**
 * Editing a member's own details — their name and photo, phone number,
 * notification preferences and emergency information.
 *
 * Unlike the other actions this one carries no call of its own: it gates a
 * whole page of sections, each of which saves through its own endpoint. The
 * sections fall back to a read-only view when it is not allowed.
 */
export function useEditProfileAction(
    props: UseEditProfileActionProps
): UseEditProfileActionData {
    const { user } = props;

    const { t } = useCompaniesTranslation();

    const canEditProfile = user.capabilities.canEditProfile;
    return useMemo(() => {
        if (!canEditProfile.allowed) {
            return {
                isAllowed: false,
                reason:
                    canEditProfile.reason ??
                    t('actions.edit_profile.reason_not_permitted')
            };
        }

        return { isAllowed: true, reason: '' };
    }, [canEditProfile, t]);
}
